import { createLegacyAccessKeys } from "../../navigation-v2/app/legacyAccessAdapter";
import {
  AKONGO_DEEP_LINK_DESTINATIONS,
  AKONGO_MENU_DESTINATIONS,
  FULLCARE_CANDIDATE_DESTINATIONS,
  FULLCARE_DEEP_LINK_DESTINATIONS,
  FULLCARE_MENU_DESTINATIONS,
} from "../../navigation-v2/registry/fullCareRegistry";

/**
 * Garde des routes Classique d'administration (audit des droits soigneur,
 * recette 12.1.0). Les routes V1 n'avaient aucune garde : seul le menu masquait
 * les outils AKONGO et l'administration du parc, qu'un Auditeur ouvrait par
 * l'URL. Le critère est celui de la Nouvelle et du menu : les clés d'accès
 * déduites du menu généré (`createLegacyAccessKeys`), reportées sur les
 * destinations du registre de navigation par leur adresse V1.
 */

// Clé réservée aux outils internes AKONGO sans destination dans le registre
// (maintenance, diagnostics de traduction) : même règle que la section
// « Administration AWM » du menu (isAkongoAdminAllowed).
export const AKONGO_MEMBER_ACCESS_KEY = "AKONGO_MEMBER";

// Destinations du parc réservées aux administrateurs, gardées en Classique.
const PARK_ADMIN_ACCESS_KEYS = new Set(["ZOO_MANAGEMENT", "USER_CREATION"]);

// Écrans du parc refusés au soigneur par le menu et la Nouvelle mais ouverts par
// l'URL en Classique (re-test agent 13, sécu D3) : météo, exercices de
// training, gestion et création de grilles. /fast-eval/grids et
// /fast-eval/import sont les alias Classique de la bibliothèque et de l'import.
// Collection ZIMS (COL-41) : /ZimsCollection montrait « Synchroniser la
// collection » au soigneur ; réservée comme au menu (admin + synchro activée).
const PARK_FEATURE_DESTINATION_IDS = new Set([
  "integration.weather",
  "integration.zims",
  "training.exercises",
  "welfare.grids",
  "welfare.grid-builder",
  "welfare.grid-editor",
  "welfare.grid-import",
]);

// Routes V1 d'outils AKONGO absentes du registre V2.
const UNREGISTERED_LEGACY_ROUTES = Object.freeze({
  "/quizzmanagementview": "QUESTIONNAIRE_ADMIN",
  "/quizzlistview": "QUESTIONNAIRE_ADMIN",
  "/quizzlistview2": "QUESTIONNAIRE_ADMIN",
  "/debugview": AKONGO_MEMBER_ACCESS_KEY,
  "/rafikitranslationdebug": AKONGO_MEMBER_ACCESS_KEY,
  "/ethogramtranslationdebug": AKONGO_MEMBER_ACCESS_KEY,
  "/contexttranslationdebug": AKONGO_MEMBER_ACCESS_KEY,
  "/deferredobsdebug": AKONGO_MEMBER_ACCESS_KEY,
  "/missingeffectivetimestamp": AKONGO_MEMBER_ACCESS_KEY,
});

const firstSegment = (value) => {
  const segment = String(value || "").split("?")[0].replace(/^\/+/, "").split("/")[0];
  return segment ? `/${segment.toLowerCase()}` : "/";
};

const buildGuardedRoutes = () => {
  const routes = new Map(Object.entries(UNREGISTERED_LEGACY_ROUTES));
  const addDestination = (destination) => {
    const pattern = destination?.versions?.v1?.pattern;
    if (!pattern) return;
    routes.set(firstSegment(pattern), destination.accessKey);
  };
  [...AKONGO_MENU_DESTINATIONS, ...AKONGO_DEEP_LINK_DESTINATIONS]
    .filter((destination) => String(destination?.id || "").startsWith("akongo."))
    .forEach(addDestination);
  FULLCARE_MENU_DESTINATIONS
    .filter((destination) => PARK_ADMIN_ACCESS_KEYS.has(destination?.accessKey))
    .forEach(addDestination);
  [...FULLCARE_MENU_DESTINATIONS, ...FULLCARE_DEEP_LINK_DESTINATIONS, ...FULLCARE_CANDIDATE_DESTINATIONS]
    .filter((destination) => PARK_FEATURE_DESTINATION_IDS.has(destination?.id))
    .forEach(addDestination);
  routes.set("/fast-eval", "GRID_MANAGEMENT");
  return routes;
};

export const LEGACY_GUARDED_ROUTES = buildGuardedRoutes();

// Clé d'accès exigée par une adresse Classique, ou null si la route n'est pas gardée.
export const resolveLegacyRouteAccessKey = (pathname) =>
  LEGACY_GUARDED_ROUTES.get(firstSegment(pathname)) || null;

export const isAkongoTeamViewer = (viewer, userRole) =>
  viewer?.akongoStaff === true ||
  viewer?.akongoRank === "board" ||
  (viewer?.zooId === "akongo" && userRole === "superAdmin");

/**
 * @returns {{ status: "open" | "pending" | "forbidden", accessKey: string|null }}
 */
export const evaluateLegacyRouteAccess = ({ pathname, viewer, zoo = {} }) => {
  const accessKey = resolveLegacyRouteAccessKey(pathname);
  if (!accessKey) return { status: "open", accessKey };
  // L'équipe AKONGO garde tous les outils, y compris la fiche d'un autre parc.
  if (isAkongoTeamViewer(viewer, zoo.userRole)) return { status: "open", accessKey };
  // Rôle et offre du parc arrivent avec le contexte chargé par la barre latérale.
  if (!zoo.productScopeReady) {
    return { status: zoo.zooOptions?.scopeLoadFailed ? "forbidden" : "pending", accessKey };
  }
  if (accessKey === AKONGO_MEMBER_ACCESS_KEY) return { status: "forbidden", accessKey };
  const keys = createLegacyAccessKeys({
    viewer,
    userRole: zoo.userRole,
    zooOptions: zoo.zooOptions,
    zooTier: zoo.zooTier,
  });
  return { status: keys.has(accessKey) ? "open" : "forbidden", accessKey };
};

// Texte de refus : celui de l'écran équivalent de la Nouvelle.
export const legacyRouteForbiddenDescriptionKey = (accessKey) => {
  if (accessKey === "ZOO_MANAGEMENT") return "zooDetails.v2.states.forbiddenDescription";
  if (accessKey === "GRID_MANAGEMENT") return "resourceEditorsV2.forbiddenGridDescription";
  if (accessKey === "WEATHER" || accessKey === "ZIMS_COLLECTION") return "parkIntegrationsV2.states.forbiddenDescription";
  if (accessKey === "TRAINING_EXERCISES") return "resourcesV2.states.forbiddenDescription";
  if (accessKey === "USER_CREATION") return "usersV2.states.forbiddenDescription";
  return "akongoAdminV2.states.forbiddenDescription";
};
